'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import {
  applyCategoryFilterChange,
  type CategoryFilterState,
  type CategoryFilterUpdate,
} from '@eliadmin/core/src/components/categories/category-query-state';
import { activeFilters } from '@eliadmin/core/src/components/dlz/category/active-filters';
import { DialogOverlay } from '@eliadmin/core/src/components/dlz/dialogs/dialog-overlay';
import { SHEET } from '@eliadmin/core/src/components/dlz/dialogs/sheet';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations, type TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { FilterGroups } from './filter-groups';
import { useProductCount } from './product-count';

/** Everything the drawer can select (and the header badge counts), cleared by „Resetovať“. */
const CLEARED = {
  subCategories: [],
  manufacturers: [],
  availabilities: [],
  statuses: [],
  attributes: [],
  price_min: 0,
  price_max: 0,
  discounted: false,
} satisfies Partial<CategoryFilterState>;

// The badge shows only how many values are selected, the chip labels are not needed.
const NO_LABELS = { price: () => '', discounted: '' };

const PLURAL = new Intl.PluralRules('sk');
const NUMBER = new Intl.NumberFormat('sk');

function showProducts(total: number, t: TranslateFn) {
  const form = PLURAL.select(total);
  const noun = form === 'one' ? 'produkt' : form === 'few' ? 'produkty' : t('text173');
  return `${t('text2076')} ${NUMBER.format(total)} ${noun}`;
}

/** Ids whose state differs between two selections (the store toggles them one by one). */
const changed = (from: number[], to: number[]) => [
  ...from.filter(id => !to.includes(id)),
  ...to.filter(id => !from.includes(id)),
];

/**
 * „Filtrovať“ button of the listing toolbar (Figma 60091:3255; phones 60438:85569: full width,
 * 48 px) and the filter drawer (Figma „Open Filter“ 60093:4632; phones 60438:87781: a bottom sheet
 * 44 px below the top of the screen): the groups edit a copy of the store filter, „Zobraziť N
 * produktov“ applies it (N counted for the pending selection), closing the drawer any other way
 * discards it.
 */
export function CategoryFilterDrawer({ className }: { className?: string }) {
  const { t } = useTranslations();
  const category = useCategoryStore(s => s.category);
  const filter = useCategoryStore(s => s.filter);
  const setFilter = useCategoryStore(s => s.setFilter);
  const setManufacturers = useCategoryStore(s => s.setManufacturers);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(filter);
  const { total, counting } = useProductCount(category.id, pending, open);
  const result = counting || total === undefined ? null : showProducts(total, t);
  const selected = activeFilters(category, pending, NO_LABELS).length;

  const onOpenChange = (next: boolean) => {
    // Every opening starts from the applied filter.
    if (next) setPending(filter);
    setOpen(next);
  };

  const change = (update: CategoryFilterUpdate) =>
    setPending(current => applyCategoryFilterChange(current, update));

  const apply = () => {
    if (pending !== filter) {
      setFilter({
        availabilities: pending.availabilities,
        attributes: pending.attributes,
        price_min: pending.price_min,
        price_max: pending.price_max,
        discounted: pending.discounted,
      });
      setManufacturers(pending.manufacturers);
      for (const status of changed(filter.statuses, pending.statuses)) setFilter({ status });
      for (const subCategory of changed(filter.subCategories, pending.subCategories)) {
        setFilter({ subCategory });
      }
    }
    setOpen(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild>
        <Button
          variant="secondary"
          iconLeft="sliders"
          className={cn('h-12 w-full md:h-[52px] md:w-auto', className)}
        >
          {t('text174')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <DialogOverlay />
        <Dialog.Content
          aria-describedby={undefined}
          // Focus the panel itself (announces the title) rather than the close button.
          onOpenAutoFocus={event => {
            event.preventDefault();
            (event.currentTarget as HTMLElement).focus();
          }}
          className={cn(SHEET, 'md:w-[514px]')}
        >
          <div className="flex items-center gap-3 border-b border-[#E2E4E7] px-4 py-6 md:p-6">
            <div className="flex flex-1 items-center gap-3">
              <Dialog.Title className="dlz-h3 text-[#0F1012]">{t('text174')}</Dialog.Title>
              {selected ? (
                <span className="dlz-label flex h-6 min-w-6 items-center justify-center rounded-full bg-[#D7191F] px-1.5 text-white">
                  <span className="dlz-trim">{selected}</span>
                </span>
              ) : null}
            </div>
            <Dialog.Close
              aria-label={t('text272')}
              className="-m-2.5 rounded-full p-2.5 text-[#B9BDC3] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
            >
              <Icon name="x" size={24} />
            </Dialog.Close>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-4 py-6 [scrollbar-color:#E2E4E7_transparent] [scrollbar-width:thin] md:p-6">
            <FilterGroups filter={pending} onChange={change} />
          </div>

          {/*
           * Phones: one row of 48 px buttons, 8 px apart (Figma 60438:87855) – the primary takes
           * the free space. A long count („Zobraziť 5 954 produktov“) does not fit next to
           * „Resetovať“; the buttons then wrap into two full-width rows.
           */}
          <div className="flex flex-wrap gap-2 border-t border-[#E2E4E7] px-4 py-6 md:flex-nowrap md:gap-3 md:p-6">
            <Button
              onClick={apply}
              aria-busy={counting || undefined}
              className="h-12 flex-[100_1_auto] px-4 md:h-[52px] md:flex-1 md:px-6"
            >
              {counting ? <Spinner /> : null}
              {result ?? t('text1835')}
            </Button>
            {/* Announces each settled count once, not the „Zobraziť produkty“ in between. */}
            <span role="status" className="sr-only">
              {result}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPending(current => ({ ...current, ...CLEARED }))}
              className="h-12 flex-auto md:h-[52px] md:flex-initial"
            >
              {t('text3850')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
